/**
 * Tokenförnyelse i exakt ett flöde åt gången.
 *
 * B2B-realmen har `revoke_refresh_token`: ett refresh token kan bara lösas in
 * en gång. oidc-client-ts egna `automaticSilentRenew` förnyar vid
 * "expiring" (60 s före utgång) och vår lyssnare på "expired" förnyade en
 * gång till – i en bakgrundsflik (strypta timers) eller efter viloläge
 * avfyras båda i samma tick, båda läser samma refresh token och Keycloak
 * avvisar det andra anropet med invalid_grant. oidc-react svarar på ett
 * sådant fel med signoutRedirect (`autoSignOut`), vilket avslutar hela
 * SSO-sessionen – därav "utloggad efter några minuter".
 *
 * Här äger vi därför hela förnyelsen själva (automaticSilentRenew av):
 * - "expiring" och "expired" delar ett pågående signinSilent-anrop.
 * - Misslyckas förnyelsen när tokenet gått ut görs en redirect-inloggning
 *   med aktuell URL som state. SSO-sessionen hos Keycloak lever kvar, så
 *   användaren kommer tillbaka utan lösenordsprompt – en omladdning i
 *   stället för en utloggning.
 */

export interface RenewalUserManager {
  events: {
    addAccessTokenExpiring(cb: () => void): unknown;
    removeAccessTokenExpiring(cb: () => void): void;
    addAccessTokenExpired(cb: () => void): unknown;
    removeAccessTokenExpired(cb: () => void): void;
  };
  signinSilent(): Promise<unknown>;
  signinRedirect(args: { state: string }): Promise<void>;
}

export interface SessionRenewalOptions {
  /** URL som användaren ska tillbaka till efter en redirect-inloggning. */
  currentUrl: () => string;
}

export interface SessionRenewal {
  start(): void;
  stop(): void;
}

const felText = (err: unknown) =>
  err instanceof Error ? err.message : String(err);

export function createSessionRenewal(
  userManager: RenewalUserManager,
  { currentUrl }: SessionRenewalOptions,
): SessionRenewal {
  let inFlight: Promise<unknown> | null = null;

  const renew = () => {
    if (!inFlight) {
      inFlight = userManager.signinSilent().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };

  const onExpiring = () => {
    // Nytt försök görs automatiskt när "expired" avfyras.
    renew().catch((err) => {
      console.warn(
        'Tyst förnyelse misslyckades, nytt försök vid tokenutgång:',
        felText(err),
      );
    });
  };

  const onExpired = () => {
    renew()
      .catch((err) => {
        console.warn(
          'Tyst förnyelse misslyckades vid tokenutgång, loggar in igen via Keycloak:',
          felText(err),
        );
        return userManager.signinRedirect({ state: currentUrl() });
      })
      .catch((err) => {
        console.error('Kunde inte återautentisera:', err);
      });
  };

  return {
    start() {
      userManager.events.addAccessTokenExpiring(onExpiring);
      userManager.events.addAccessTokenExpired(onExpired);
    },
    stop() {
      userManager.events.removeAccessTokenExpiring(onExpiring);
      userManager.events.removeAccessTokenExpired(onExpired);
    },
  };
}
